/**
 * CLE-2971 — the Service Type switch's one event bus between the page and `EligibilityButton`.
 *
 * The API switches the Service Type (pVerify practice type code) itself when an order's CPT codes say
 * the check should run under another one. Two things on the page still need to ask a side's
 * `EligibilityButton` to re-run, and both ask with `PRACTICE_TYPE_RUN_EVENT` (the same pattern as
 * `outOfNetworkExceptionEvent.ts`):
 *  - the notice's "Keep …" button: re-run under the code the estimator chose, with `keepPracticeType`;
 *  - the page trigger: CPT codes were added after eligibility ran, so re-run once and let the API switch.
 *
 * `PRACTICE_TYPE_RUN_COMPLETED_EVENT` goes the other way: the button announces each run it stored in
 * this page session, which is how the page trigger knows a run happened here (not on loading a draft,
 * copy or redo) and whether that run was a "Keep" run.
 *
 * `PRACTICE_TYPE_RUN_SETTLED_EVENT` is announced once a side's runs have settled — success OR failure
 * (no response, an error, an inactive plan, a rejected request) — and no held run follows. The page
 * trigger waits on it to release its hold on the page's eligibility activity and to take down its
 * "Re-running eligibility…" toast: a failed run never announces completion, so completion alone could
 * leave both up for good.
 */
export const PRACTICE_TYPE_RUN_EVENT = "runEligibilityUnderPracticeType";
export const PRACTICE_TYPE_RUN_COMPLETED_EVENT =
  "eligibilityRunUnderPracticeTypeCompleted";
export const PRACTICE_TYPE_RUN_SETTLED_EVENT =
  "eligibilityRunUnderPracticeTypeSettled";

export interface IPracticeTypeRunEventDetail {
  isSecondaryInsurance: boolean;
  /** Set the side's Service Type to this code before the run; omitted = run under the current one. */
  practiceTypeCode?: string;
  /** Run exactly this Service Type: the API must not switch it. */
  keepPracticeType?: boolean;
}

export interface IPracticeTypeRunCompletedEventDetail {
  isSecondaryInsurance: boolean;
  /** The Service Type the stored check RAN under (the switch's target when the API switched). */
  practiceTypeCode: string;
  keepPracticeType: boolean;
  /**
   * The CPT codes the run was SENT with. Codes typed while it was in flight are not in it, so the page
   * trigger compares against this, not against the codes on the form when the run completes.
   */
  procedureCodes?: string[];
}

export interface IPracticeTypeRunSettledEventDetail {
  isSecondaryInsurance: boolean;
}

/**
 * Returns whether a side's `EligibilityButton` took the request (ran it, or held it behind the run in
 * flight): the button calls `preventDefault()` on it. False when no button for that side is mounted, so
 * the page trigger never waits for a run that will not happen.
 */
export const requestPracticeTypeRun = (
  detail: IPracticeTypeRunEventDetail
): boolean =>
  !window.dispatchEvent(
    new CustomEvent<IPracticeTypeRunEventDetail>(PRACTICE_TYPE_RUN_EVENT, {
      detail,
      cancelable: true,
    })
  );

export const announcePracticeTypeRunCompleted = (
  detail: IPracticeTypeRunCompletedEventDetail
) => {
  window.dispatchEvent(
    new CustomEvent<IPracticeTypeRunCompletedEventDetail>(
      PRACTICE_TYPE_RUN_COMPLETED_EVENT,
      { detail }
    )
  );
};

export const announcePracticeTypeRunSettled = (
  detail: IPracticeTypeRunSettledEventDetail
) => {
  window.dispatchEvent(
    new CustomEvent<IPracticeTypeRunSettledEventDetail>(
      PRACTICE_TYPE_RUN_SETTLED_EVENT,
      { detail }
    )
  );
};
